import { useMemo } from "react";
import { Message } from "../ai-elements/message";
import { renderToolCall } from "./tool-calls-progress";
import { Version } from "./version";
import type {
  ImagineUIMessage,
  ImagineUIToolParts,
} from "@imagine/ai-service/shared-types";
import { imagineStore } from "@/stores/imagine.store";
import { Action, Reasoning } from "./in-chat-components";
import { type ChatStatus } from "ai";
import { SuggestionList } from "../chat-panel/suggestions";
import { ToolCallsGroup } from "./tool-calls-group";
import { RenderTextPart } from "./render-text-part";
import { AnimatePresence, motion } from "motion/react";

const AnimatedMessage = motion(Message);

// Assign a weight to each type for sorting
function getTypeOrder(type: ImagineUIMessage["parts"][number]["type"]) {
  if (type === "data-version") return 2;
  if (type === "data-message-billing") return 3;
  if (type === "data-suggestions") return 4;
  return 1;
}

function isGroupableAction(part: ImagineUIMessage["parts"][number]): boolean {
  if (part.type.startsWith("tool-")) return true;
  if (part.type === "data-custom-progress-action") return true;
  return false;
}

function MessagePartsRenderer({
  messageId,
  parts,
  isLatestMessage,
  chatStatus,
  isWorkspaceInProgress,
}: {
  messageId: string;
  parts: ImagineUIMessage["parts"];
  isLatestMessage: boolean;
  chatStatus: ChatStatus;
  isWorkspaceInProgress: boolean;
}) {
  const partsToRender = Array<{
    type: string;
    component: React.ReactNode;
  }>();

  let hasVersionPart = false;
  let currentActionGroup: Array<React.ReactNode> = [];
  let hasInProgressInCurrentGroup = false;

  const flushActionGroup = () => {
    if (currentActionGroup.length > 0) {
      // Extract a stable ID from the first action in the group
      const firstAction = currentActionGroup[0];
      const firstActionKey =
        (firstAction &&
          typeof firstAction === "object" &&
          "key" in firstAction &&
          firstAction.key) ||
        partsToRender.length;

      partsToRender.push({
        type: "tool-calls-group",
        component: (
          <ToolCallsGroup
            key={`tool-calls-group-${messageId}-${firstActionKey}`}
            isInProgress={hasInProgressInCurrentGroup}
            hasVersion={hasVersionPart}
            chatStatus={chatStatus}
            isLatestMessage={isLatestMessage}
          >
            {currentActionGroup}
          </ToolCallsGroup>
        ),
      });
      currentActionGroup = [];
      hasInProgressInCurrentGroup = false;
    }
  };

  for (const [index, part] of parts.entries()) {
    if (part.type === "step-start") {
      continue;
    }

    if (isGroupableAction(part)) {
      // Handle tool calls
      if (part.type.startsWith("tool-")) {
        const p = part as ImagineUIToolParts;
        const isInProgress =
          p.state !== "output-available" && p.state !== "output-error";
        const content = renderToolCall(p);
        if (!content) {
          continue;
        }

        if (isInProgress) {
          hasInProgressInCurrentGroup = true;
        }

        // Add to current group
        currentActionGroup.push(
          <Action
            key={`tool-call-${p.toolCallId}`}
            id={p.toolCallId}
            isInProgress={isInProgress}
          >
            {content}
          </Action>,
        );
      }
      // Handle custom progress actions
      else if (part.type === "data-custom-progress-action" && part.id) {
        const text = part.data.text;
        if (!text) {
          continue;
        }

        // Add to current group
        currentActionGroup.push(
          <Action key={`custom-progress-action-${part.id}`} id={part.id}>
            {text}
          </Action>,
        );
      }
    } else {
      // Non-groupable part - flush any pending group first
      flushActionGroup();

      if (part.type === "reasoning") {
        partsToRender.push({
          type: part.type,
          component: (
            <Reasoning
              key={`reasoning-part-${index}-${messageId}`}
              text={part.text}
              isThinking={part.state === "streaming"}
            />
          ),
        });
      }

      if (part.type === "text") {
        partsToRender.push({
          type: part.type,
          component: (
            <RenderTextPart
              key={`text-part-${index}-${messageId}`}
              part={part}
            />
          ),
        });
      }

      if (part.type === "data-version") {
        hasVersionPart = true;

        if (
          isLatestMessage &&
          (chatStatus === "streaming" || chatStatus === "submitted")
        ) {
          continue;
        }

        partsToRender.push({
          type: part.type,
          component: (
            <div
              key={`version-part-${index}-${messageId}`}
              className="flex flex-col gap-1.5"
            >
              <Version
                messageId={messageId}
                data={part.data}
                canRestore={
                  !isLatestMessage &&
                  chatStatus !== "streaming" &&
                  chatStatus !== "submitted" &&
                  !isWorkspaceInProgress
                }
                isLatestVersion={isLatestMessage}
              />
            </div>
          ),
        });
      }

      if (
        part.type === "data-suggestions" &&
        chatStatus === "ready" &&
        isLatestMessage
      ) {
        partsToRender.push({
          type: part.type,
          component: (
            <SuggestionList
              key={`sugg-${part.id}-${messageId}`}
              part={part.data}
            />
          ),
        });
      }
    }
  }

  // Flush any remaining actions at the end
  flushActionGroup();

  // Sort to ensure versions and suggestions come last, everything else maintains order
  const sortedPartsToRender = [...partsToRender].sort((a, b) => {
    return (
      getTypeOrder(a.type as ImagineUIMessage["parts"][number]["type"]) -
      getTypeOrder(b.type as ImagineUIMessage["parts"][number]["type"])
    );
  });

  return sortedPartsToRender;
}

export function AssistantMessage({
  message,
  isLatestMessage,
}: {
  message: ImagineUIMessage;
  isLatestMessage: boolean;
}) {
  const chatStatus = imagineStore.use.chat.status();
  const isWorkspaceInProgress = imagineStore.use.workspaceState.isInProgress();

  const renderedParts = useMemo(() => {
    const renderedParts = MessagePartsRenderer({
      messageId: message.id,
      parts: message.parts,
      isLatestMessage,
      chatStatus,
      isWorkspaceInProgress,
    });
    return renderedParts;
  }, [
    message.parts,
    message.id,
    isLatestMessage,
    chatStatus,
    isWorkspaceInProgress,
  ]);

  // Only render the Message wrapper if there's actual content to display
  if (renderedParts.length === 0) {
    return null;
  }

  return (
    <AnimatePresence>
      <AnimatedMessage
        from="assistant"
        key={message.id}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        transition={{ duration: 0.25 }}
        className="flex-col items-start gap-4"
      >
        {renderedParts.map((part) => part.component)}
      </AnimatedMessage>
    </AnimatePresence>
  );
}
